import { DragOverlay as DndDragOverlay } from "@dnd-kit/core";
import { Calendar, Clock, GripVertical } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface ProjectDragOverlayProps {
  name: string;
  client: string;
  deadline: string;
  hoursSpent: number;
  status: "backlog" | "in_progress" | "review" | "done";
}

interface TaskDragOverlayProps {
  title: string;
  description?: string | null;
  status: string;
}

const statusColors = {
  backlog: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/10 text-primary",
  review: "bg-warning/10 text-warning",
  done: "bg-success/10 text-success",
};

const statusLabels = {
  backlog: "Backlog",
  in_progress: "Em Andamento",
  review: "Revisão",
  done: "Concluído",
};

const taskStatusColors: Record<string, string> = {
  todo: "border-l-muted-foreground",
  doing: "border-l-primary",
  done: "border-l-success",
};

export function ProjectDragOverlay({
  name,
  client,
  deadline,
  hoursSpent,
  status,
}: ProjectDragOverlayProps) {
  return (
    <Card className="shadow-lg rotate-3 w-[280px]">
      <CardHeader className="pb-2">
        <div className="flex items-start gap-2">
          <GripVertical className="h-4 w-4 mt-1 text-muted-foreground" />
          <div className="space-y-1">
            <h3 className="font-semibold leading-none">{name}</h3>
            <p className="text-sm text-muted-foreground">{client}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Badge variant="secondary" className={statusColors[status]}>
          {statusLabels[status]}
        </Badge>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {deadline && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              <span>{deadline}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span>{hoursSpent}h</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TaskDragOverlay({ title, description, status }: TaskDragOverlayProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg border border-l-4 bg-card p-3 shadow-lg rotate-3 w-[250px]",
        taskStatusColors[status] || "border-l-muted"
      )}
    >
      <GripVertical className="h-4 w-4 mt-0.5 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
